import React from 'react';
import SEO from '../../components/ui/SEO';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { Activity, CheckCircle } from 'lucide-react';
export default function MentalCapacity() {
  return <main className="min-h-screen bg-white" data-id="element-1121" data-mp="gnf0fjal8omy">
      <SEO title="Mental Capacity Assessments | COP3 and DoLS Reports" description="Independent mental capacity assessments for Court of Protection, DoLS, testamentary and litigation capacity. Instruct Dr Syed today." path="/services/mental-capacity" keywords={['mental capacity assessment', 'capacity to litigate assessment', 'COP3 psychiatric report', 'DoLS assessment']} imageAlt="Mental capacity assessment reports for legal proceedings" schema={{
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Mental Capacity Assessments',
      serviceType: 'Mental capacity assessment',
      url: 'https://medicolegal-psychiatry.co.uk/services/mental-capacity',
      areaServed: {
        '@type': 'AdministrativeArea',
        name: 'England and Wales'
      },
      provider: {
        '@type': 'Organization',
        name: 'Medicolegal Psychiatry',
        url: 'https://medicolegal-psychiatry.co.uk'
      },
      audience: {
        '@type': 'Audience',
        name: 'Legal professionals'
      },
      description: 'Decision-specific mental capacity assessments under the Mental Capacity Act 2005, including COP3, DoLS, testamentary and litigation capacity.'
    }} data-id="element-1122" data-mp="bp3q77gdsc47" />
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-1123" data-mp="2arolnufs4iyw">
        <div className="container mx-auto px-4" data-id="element-1124" data-mp="1dbsnutyels3y">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-4 text-center" data-id="element-1125" data-mp="1dazctgsewgg0">
            Mental Capacity Assessments UK
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-1126" data-mp="2lkq7tq43v5kd">
            Essential Legal Clarity & Protection
          </p>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12" data-id="element-1127" data-mp="2udblwswsko2v">
        <div className="container mx-auto px-4" data-id="element-1128" data-mp="2ucib9guppefg">
          <div className="max-w-3xl mx-auto" data-id="element-1129" data-mp="2ufecbe1uwj84">
            <p className="text-slate-dark text-lg mb-6" data-id="element-1130" data-mp="2qd150l7god98">
              In complex legal matters, a clear understanding of an individual's
              mental capacity is paramount. Dr. Imtiaz Syed provides meticulous,
              impartial, and expert Mental Capacity Assessments, strictly
              adhering to the Mental Capacity Act 2005. Our goal is to equip
              legal professionals with robust evidence, ensuring decisions align
              with an individual's best interests and uphold their rights.
            </p>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1131" data-mp="3n4bnyybkbhrw">
              Why Instruct Dr. Imtiaz Syed for Mental Capacity Assessments?
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1132" data-mp="3qild1jk0k2sg">
              Dr. Syed brings deep clinical expertise and legal acumen to every
              capacity assessment, ensuring:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-1133" data-mp="3gna8tv68edyy">
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1134" data-mp="3lkjvj328kbj3">
                <div className="flex items-start" data-id="element-1135" data-mp="3ln63v4wyav2d">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1136" data-mp="d5084ko8xnby" />
                  <div data-id="element-1137" data-mp="i438vsx717e4">
                    <h3 className="font-bold text-primary" data-id="element-1138" data-mp="i3bgss2t1rsl">Legal Compliance</h3>
                    <p className="text-slate" data-id="element-1139" data-mp="3fyswwpiz9z1">
                      All assessments strictly follow the Mental Capacity Act
                      2005 and relevant case law.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1140" data-mp="w21fgc59vzj2">
                <div className="flex items-start" data-id="element-1141" data-mp="10vw29og6cumd">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1142" data-mp="38efl099fagem" />
                  <div data-id="element-1143" data-mp="38cb7czlq2315">
                    <h3 className="font-bold text-primary" data-id="element-1144" data-mp="34cu1i52dv3vg">
                      Impartial & Objective Insights
                    </h3>
                    <p className="text-slate" data-id="element-1145" data-mp="2zesghelecuhc">
                      Providing unbiased, evidence-based opinions you can trust.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1146" data-mp="3s1vdu3ec6z60">
                <div className="flex items-start" data-id="element-1147" data-mp="3n2s80gqj8owz">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1148" data-mp="3n43lx9dozd1t" />
                  <div data-id="element-1149" data-mp="3ibaicotymarb">
                    <h3 className="font-bold text-primary" data-id="element-1150" data-mp="3iaqwwy53p48j">
                      Thorough & Sensitive Approach
                    </h3>
                    <p className="text-slate" data-id="element-1151" data-mp="en8kzzggg2qd">
                      Assessments are comprehensive, conducted with the utmost
                      respect and empathy for the individual.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1152" data-mp="haav2ir2lo6g">
                <div className="flex items-start" data-id="element-1153" data-mp="m7sv1jn40t2b">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1154" data-mp="r6461xp559m4" />
                  <div data-id="element-1155" data-mp="r877ddvqup65">
                    <h3 className="font-bold text-primary" data-id="element-1156" data-mp="w4xkptg5z6nv">
                      Courtroom-Ready Reports
                    </h3>
                    <p className="text-slate" data-id="element-1157" data-mp="105r9ep1fkpht">
                      Clear, concise, and robust reports designed to withstand
                      scrutiny.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1158" data-mp="1evozjaix7p9n">
              Key Mental Capacity Assessments Provided
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1159" data-mp="1jw2x1ymwbgtf">
              Dr. Syed offers expert evaluations across critical areas,
              including:
            </p>
            <ul className="space-y-3 mb-8 text-slate-dark" data-id="element-1160" data-mp="3kwrch5wi9o8l">
              <li className="flex items-start" data-id="element-1161" data-mp="3kuwsbtt2605k">
                <span className="text-secondary font-bold mr-2" data-id="element-1162" data-mp="3fypk1ud6wckg">•</span>
                <span data-id="element-1163" data-mp="3tpb77v9of8o5">
                  <strong data-id="element-1164" data-mp="3tpul90w7hcww">Capacity to Litigate:</strong> Assessing the ability
                  to understand legal proceedings and instruct legal counsel.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1165" data-mp="7hlaa9n1hbqj">
                <span className="text-secondary font-bold mr-2" data-id="element-1166" data-mp="7g9z3or9l1pe">•</span>
                <span data-id="element-1167" data-mp="2hyqfhcpgx8r">
                  <strong data-id="element-1168" data-mp="2irz4nv85wtj">Capacity for Residence & Care Decisions:</strong>{' '}
                  Evaluating the ability to choose living arrangements and care.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1169" data-mp="chz7sxr0l3ab">
                <span className="text-secondary font-bold mr-2" data-id="element-1170" data-mp="2i9mj30kos3lr">•</span>
                <span data-id="element-1171" data-mp="2ibz79vh1fa79">
                  <strong data-id="element-1172" data-mp="2e92kdskgsrxi">Capacity to Manage Property & Affairs (COP3):</strong>{' '}
                  Essential for financial decisions and Court of Protection
                  applications.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1173" data-mp="284xt2n2gxvag">
                <span className="text-secondary font-bold mr-2" data-id="element-1174" data-mp="1ueud2du6mxtg">•</span>
                <span data-id="element-1175" data-mp="1ucq8rusdfwsn">
                  <strong data-id="element-1176" data-mp="1zdxfrrmowry9">Testamentary Capacity:</strong> Determining mental
                  capacity for making or changing a will.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1177" data-mp="1kir9r4nlgt4x">
                <span className="text-secondary font-bold mr-2" data-id="element-1178" data-mp="1pfztvasaz78t">•</span>
                <span data-id="element-1179" data-mp="1bfyi30ai2fek">
                  <strong data-id="element-1180" data-mp="1bgi34pvg5jwt">Capacity for Medical Treatment Decisions:</strong>{' '}
                  Assessing the ability to consent to or refuse medical care.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1181" data-mp="35tg4ul32z88j">
                <span className="text-secondary font-bold mr-2" data-id="element-1182" data-mp="35urdozbdv66i">•</span>
                <span data-id="element-1183" data-mp="3arri75sl40vi">
                  <strong data-id="element-1184" data-mp="3asayki2l6h7f">
                    Deprivation of Liberty Safeguards (DoLS) & Section 21A
                    Challenges:
                  </strong>{' '}
                  Expert assessments for care arrangements where liberty may be
                  restricted.
                </span>
              </li>
            </ul>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1185" data-mp="9gz1aqsonq51">
              Our Seamless Process & Robust Reports
            </h2>
            <p className="text-slate-dark mb-6" data-id="element-1186" data-mp="4ifdekuj44a1">
              Our assessment process is thorough and efficient, involving
              careful review of documentation and sensitive clinical interviews.
              Dr. Syed is flexible, offering both secure remote assessments
              across the UK and in-person consultations in specific regions
              (West Midlands, East Midlands, Greater London, or by arrangement).
              The resulting reports are always clear, legally relevant, and
              provide precise findings and actionable insights for your case.
            </p>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1187" data-mp="32szgnvybalht">
              Partnering for Just Outcomes
            </h2>
            <p className="text-slate-dark mb-6" data-id="element-1188" data-mp="3cjaldkptkypr">
              Dr. Imtiaz Syed is your trusted partner for navigating mental
              capacity complexities. Rely on his expertise to provide the
              clarity needed for informed legal decisions and to ensure
              individual rights are always protected.
            </p>

            {/* Related Services */}
            <div className="mb-10 border-t border-gray-200 pt-8" data-id="element-1189" data-mp="2ovu3lc06kmd2">
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1190" data-mp="2sujp5rvdha7s">
                Explore Our Other Services
              </h3>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4" data-id="element-1191" data-mp="2xs1pitvbqh46">
                <Link to="/services/forensic-risk" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1192" data-mp="84kng04x7xdm">
                  Forensic & Risk Assessments
                </Link>
                <Link to="/services/specialist" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1193" data-mp="2hctjnebv9psa">
                  Psychiatric Reports for Courts & Tribunals
                </Link>
                <Link to="/services/clinical-negligence" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1194" data-mp="1tfe0ruu15lie">
                  Clinical Negligence Cases
                </Link>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg shadow-md mb-8" data-id="element-1195" data-mp="27f70ugrg5e31">
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1196" data-mp="27gi3ued2iht3">
                Ready to Instruct Dr. Syed?
              </h3>
              <p className="text-slate-dark mb-4" data-id="element-1197" data-mp="3iu2fi2q3ffjl">
                We invite you to discuss your specific case requirements.
              </p>
              <div className="flex items-center" data-id="element-1198" data-mp="176h0zvevs8ke">
                <span className="mr-2 text-slate-dark" data-id="element-1199" data-mp="135ndv499ixsk">Contact Us:</span>
                <a href="mailto:enquiries@medicolegal-psychiatry.co.uk" className="text-primary hover:text-secondary transition-colors" data-id="element-1200" data-mp="137hwgbx2adth">
                  enquiries@medicolegal-psychiatry.co.uk
                </a>
              </div>
            </div>
            <div className="text-center" data-id="element-1201" data-mp="oe4pai2c7uxl">
              <Button variant="primary" size="lg" href="/contact" data-id="element-1202" data-mp="jh4reufn5yhk">
                Book a Mental Capacity Assessment
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>;
}